import React, { useMemo, useState } from 'react';
import {
  FaArrowRight,
  FaFireExtinguisher,
  FaFirstAid,
  FaHardHat,
  FaTrafficLight,
} from 'react-icons/fa';
import { HiOutlineCheckCircle } from 'react-icons/hi2';
import { MdOutlineHealthAndSafety } from 'react-icons/md';
import { RiWaterFlashLine } from 'react-icons/ri';

import {
  Section,
  Container,
  SectionHeader,
  Title,
  Description,
  ProductsLayout,
  Sidebar,
  SidebarHeader,
  SidebarTitle,
  CategoryNavigation,
  CategoryNavButton,
  CategoryNavIcon,
  SidebarCallout,
  SidebarCalloutTitle,
  SidebarCalloutText,
  SidebarCalloutLink,
  MobileNavigation,
  MobileNavigationLabel,
  MobileSelect,
  ProductsContent,
  CategoryPanel,
  CategoryHeader,
  CategoryIcon,
  CategoryHeadingGroup,
  CategoryEyebrow,
  CategoryTitle,
  CategoryDescription,
  ProductGrid,
  ProductGroup,
  ProductGroupTitle,
  ProductList,
  ProductListItem,
  CheckIcon,
  CategoryFooter,
  CategoryNote,
  CategoryButton,
  ButtonIcon,
  AdditionalProducts,
  AdditionalIcon,
  AdditionalContent,
  AdditionalTitle,
  AdditionalText,
  AdditionalLink,
} from './ExploreProducts.styles';

const categories = [
  {
    id: 'ppe',
    name: 'PPE',
    icon: <FaHardHat />,
    description:
      'Personal protective equipment for everyday workplace hazards, specialized tasks, and job-site requirements.',
    productGroups: [
      {
        title: 'Head, Eye & Face Protection',
        products: [
          'Hard hats and safety helmets',
          'Safety glasses and goggles',
          'Face shields',
          'Welding protection',
        ],
      },
      {
        title: 'Hearing & Respiratory Protection',
        products: [
          'Earplugs and earmuffs',
          'Disposable respirators',
          'Reusable respirators',
          'Respirator accessories',
        ],
      },
      {
        title: 'Protective Clothing',
        products: [
          'High-visibility apparel',
          'Protective coveralls',
          'Safety gloves',
          'Protective footwear options',
        ],
      },
    ],
  },
  {
    id: 'first-aid',
    name: 'First Aid',
    icon: <FaFirstAid />,
    description:
      'Workplace first aid products designed to support emergency preparedness, injury response, and routine restocking.',
    productGroups: [
      {
        title: 'First Aid Kits',
        products: [
          'Workplace first aid kits',
          'Vehicle first aid kits',
          'Portable first aid kits',
          'Industry-specific kits',
        ],
      },
      {
        title: 'First Aid Supplies',
        products: [
          'Bandages and dressings',
          'Antiseptic products',
          'Burn care supplies',
          'Cold packs and emergency blankets',
        ],
      },
      {
        title: 'Emergency Equipment',
        products: [
          'Eyewash supplies',
          'Trauma supplies',
          'First aid signage',
          'Kit refill products',
        ],
      },
    ],
  },
  {
    id: 'traffic-control',
    name: 'Traffic Control',
    icon: <FaTrafficLight />,
    description:
      'Traffic and work-zone products that help improve visibility, organize movement, and support safer job sites.',
    productGroups: [
      {
        title: 'Work-Zone Equipment',
        products: [
          'Traffic cones',
          'Delineator posts',
          'Barricades',
          'Temporary fencing',
        ],
      },
      {
        title: 'Visibility Products',
        products: [
          'High-visibility vests',
          'Reflective tape',
          'Warning flags',
          'Portable warning lights',
        ],
      },
      {
        title: 'Traffic Signage',
        products: [
          'Work-zone signs',
          'Parking and facility signs',
          'Sign stands',
          'Custom safety signage inquiries',
        ],
      },
    ],
  },
  {
    id: 'fall-protection',
    name: 'Fall Protection',
    icon: <MdOutlineHealthAndSafety />,
    description:
      'Fall-protection products and accessories intended to support safer work at height and workplace prevention efforts.',
    productGroups: [
      {
        title: 'Personal Fall Protection',
        products: [
          'Full-body harnesses',
          'Lanyards',
          'Self-retracting lifelines',
          'Positioning equipment',
        ],
      },
      {
        title: 'Anchorage & Connection',
        products: [
          'Anchor points',
          'Connectors and carabiners',
          'Horizontal lifeline systems',
          'Temporary anchorage solutions',
        ],
      },
      {
        title: 'Rescue & Accessories',
        products: [
          'Fall rescue equipment',
          'Storage bags',
          'Inspection tags',
          'Replacement components',
        ],
      },
    ],
  },
  {
    id: 'fire-safety',
    name: 'Fire Safety',
    icon: <FaFireExtinguisher />,
    description:
      'Fire-safety products to help workplaces prepare for emergencies, communicate hazards, and maintain accessible equipment.',
    productGroups: [
      {
        title: 'Fire Response Equipment',
        products: [
          'Fire extinguishers',
          'Fire blankets',
          'Extinguisher cabinets',
          'Emergency response accessories',
        ],
      },
      {
        title: 'Fire Safety Signage',
        products: [
          'Fire extinguisher signs',
          'Emergency exit signs',
          'Evacuation signs',
          'Fire-door identification',
        ],
      },
      {
        title: 'Emergency Preparedness',
        products: [
          'Emergency lighting products',
          'Evacuation supplies',
          'Inspection tags',
          'Equipment mounting accessories',
        ],
      },
    ],
  },
  {
    id: 'spill-response',
    name: 'Spill Response',
    icon: <RiWaterFlashLine />,
    description:
      'Spill-control products designed to help workplaces prepare for leaks, releases, containment, and cleanup.',
    productGroups: [
      {
        title: 'Spill Kits',
        products: [
          'Universal spill kits',
          'Oil-only spill kits',
          'Chemical spill kits',
          'Vehicle spill kits',
        ],
      },
      {
        title: 'Absorbents',
        products: [
          'Absorbent pads',
          'Absorbent rolls',
          'Socks and booms',
          'Loose absorbent materials',
        ],
      },
      {
        title: 'Containment & Cleanup',
        products: [
          'Containment trays',
          'Drain covers',
          'Disposal bags',
          'Spill response signage',
        ],
      },
    ],
  },
];

export default function ExploreProducts() {
  const [activeCategoryId, setActiveCategoryId] = useState(categories[0].id);

  const activeCategory = useMemo(
    () =>
      categories.find((category) => category.id === activeCategoryId) ??
      categories[0],
    [activeCategoryId]
  );

  const handleCategoryClick = (categoryId) => {
    setActiveCategoryId(categoryId);
  };

  const handleMobileChange = (event) => {
    setActiveCategoryId(event.target.value);
  };

  return (
    <Section id="explore-products">
      <Container>
        <SectionHeader>
          {/* <Eyebrow>Explore Our Products</Eyebrow> */}

          <Title>Workplace Safety Products</Title>

          <Description>
            Explore our core safety product categories. Product availability,
            specifications, and pricing are confirmed directly with our team
            before every order.
          </Description>
        </SectionHeader>

        <MobileNavigation>
          <MobileNavigationLabel htmlFor="product-category">
            Browse by category
          </MobileNavigationLabel>

          <MobileSelect
            id="product-category"
            value={activeCategoryId}
            onChange={handleMobileChange}
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </MobileSelect>
        </MobileNavigation>

        <ProductsLayout>
          <Sidebar>
            <SidebarHeader>
              <SidebarTitle>Product Categories</SidebarTitle>
            </SidebarHeader>

            <CategoryNavigation aria-label="Product categories">
              {categories.map((category) => {
                const isActive = activeCategoryId === category.id;

                return (
                  <CategoryNavButton
                    key={category.id}
                    type="button"
                    $active={isActive}
                    aria-pressed={isActive}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    <CategoryNavIcon aria-hidden="true">
                      {category.icon}
                    </CategoryNavIcon>

                    <span>{category.name}</span>
                  </CategoryNavButton>
                );
              })}
            </CategoryNavigation>

            <SidebarCallout>
              <SidebarCalloutTitle>
                Need help finding a product?
              </SidebarCalloutTitle>

              <SidebarCalloutText>
                Tell us what you are looking for and our team can help identify
                available options.
              </SidebarCalloutText>

              <SidebarCalloutLink href="/contact-us">
                Contact Our Team
                <FaArrowRight aria-hidden="true" />
              </SidebarCalloutLink>
            </SidebarCallout>
          </Sidebar>

          <ProductsContent>
            <CategoryPanel key={activeCategory.id}>
              <CategoryHeader>
                <CategoryIcon aria-hidden="true">
                  {activeCategory.icon}
                </CategoryIcon>

                <CategoryHeadingGroup>
                  <CategoryEyebrow>Safety Products</CategoryEyebrow>

                  <CategoryTitle>{activeCategory.name}</CategoryTitle>

                  <CategoryDescription>
                    {activeCategory.description}
                  </CategoryDescription>
                </CategoryHeadingGroup>
              </CategoryHeader>

              <ProductGrid>
                {activeCategory.productGroups.map((group) => (
                  <ProductGroup key={group.title}>
                    <ProductGroupTitle>{group.title}</ProductGroupTitle>

                    <ProductList>
                      {group.products.map((product) => (
                        <ProductListItem key={product}>
                          <CheckIcon aria-hidden="true">
                            <HiOutlineCheckCircle />
                          </CheckIcon>

                          <span>{product}</span>
                        </ProductListItem>
                      ))}
                    </ProductList>
                  </ProductGroup>
                ))}
              </ProductGrid>

              <CategoryFooter>
                <CategoryNote>
                  Looking for a specific product, size, quantity, or
                  specification? Contact our team to discuss availability.
                </CategoryNote>

                <CategoryButton href="/contact-us">
                  Contact Us to Order
                  <ButtonIcon aria-hidden="true">
                    <FaArrowRight />
                  </ButtonIcon>
                </CategoryButton>
              </CategoryFooter>
            </CategoryPanel>

            <AdditionalProducts>
              <AdditionalIcon aria-hidden="true">
                <FaHardHat />
              </AdditionalIcon>

              <AdditionalContent>
                <AdditionalTitle>
                  Looking for something not listed?
                </AdditionalTitle>

                <AdditionalText>
                  The products shown represent common categories and examples,
                  not the full range that may be available. Contact us with the
                  product name, type, quantity, or workplace need, and we will
                  help explore suitable options.
                </AdditionalText>

                <AdditionalLink href="/contact-us">
                  Ask About a Product
                  <FaArrowRight aria-hidden="true" />
                </AdditionalLink>
              </AdditionalContent>
            </AdditionalProducts>
          </ProductsContent>
        </ProductsLayout>
      </Container>
    </Section>
  );
}
